import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { RealtimeSettings, AvailableFolders } from '../types';

interface RealtimeControlPanelProps {
  // Settings dialog
  settingsDialogOpen: boolean;
  setSettingsDialogOpen: (open: boolean) => void;
  settings: RealtimeSettings;
  availableFolders: AvailableFolders;
  handleSettingChange: (field: string, value: any) => void;
  isRunning: boolean;
  imageSavingEnabled: boolean;
  setImageSavingEnabled: (enabled: boolean) => void;
  isMobile: boolean;
}

const RealtimeControlPanel: React.FC<RealtimeControlPanelProps> = ({
  settingsDialogOpen,
  setSettingsDialogOpen,
  settings,
  availableFolders,
  handleSettingChange,
  isRunning,
  imageSavingEnabled,
  setImageSavingEnabled,
  isMobile,
}) => {
  return (
    <>
      {/* Settings dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">{'\uC2E4\uC2DC\uAC04 \uBAA8\uB2C8\uD130\uB9C1 \uC124\uC815'}</Typography>
          <IconButton onClick={() => setSettingsDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {'\uD30C\uC77C \uACBD\uB85C \uC124\uC815'}
            </Typography>
            <Box sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: '1fr'
            }}>
              <FormControl fullWidth size="small" disabled={isRunning}>
                <InputLabel>{'\uD559\uC2B5 \uC774\uBBF8\uC9C0 \uD3F4\uB354'}</InputLabel>
                <Select
                  value={settings.learningImageFolder}
                  label={'\uD559\uC2B5 \uC774\uBBF8\uC9C0 \uD3F4\uB354'}
                  onChange={(e) => handleSettingChange('learningImageFolder', e.target.value)}
                  disabled={isRunning}
                >
                  {availableFolders.learning.map((folder) => (
                    <MenuItem key={folder} value={folder}>
                      {folder}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={isRunning}>
                <InputLabel>ROI {'\uD30C\uC77C'}</InputLabel>
                <Select
                  value={settings.roiFile}
                  label={`ROI \uD30C\uC77C`}
                  onChange={(e) => handleSettingChange('roiFile', e.target.value)}
                  disabled={isRunning}
                >
                  {availableFolders.roi.map((file) => (
                    <MenuItem key={file} value={file}>
                      {file}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {'\uC2E4\uC2DC\uAC04 \uC124\uC815'}
            </Typography>
            <Box sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }
            }}>
              <TextField
                label="Var Threshold"
                type="number"
                value={settings.varThreshold}
                onChange={(e) => handleSettingChange('varThreshold', parseInt(e.target.value))}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 1000 }}
                disabled={isRunning}
              />
              <TextField
                label="Learning Rate"
                type="number"
                value={settings.learningRate}
                onChange={(e) => handleSettingChange('learningRate', parseFloat(e.target.value))}
                fullWidth
                size="small"
                inputProps={{ step: 0.0001, min: 0.001, max: 1 }}
                disabled={isRunning}
              />
              <TextField
                label="Iterations"
                type="number"
                value={settings.iterations}
                onChange={(e) => handleSettingChange('iterations', parseInt(e.target.value))}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 10 }}
                disabled={isRunning}
                sx={{ gridColumn: { xs: '1', sm: 'span 2' } }}
              />
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={imageSavingEnabled}
                onChange={(e) => setImageSavingEnabled(e.target.checked)}
                disabled={isRunning}
              />
            }
            label={'\uC2E4\uC2DC\uAC04 \uC774\uBBF8\uC9C0 \uC800\uC7A5'}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSettingsDialogOpen(false)}
            variant="outlined"
            fullWidth={isMobile}
          >
            {'\uB2EB\uAE30'}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default RealtimeControlPanel;
